// Amadeus ancillaries-as-step pipeline (capability: ancillariesAsStep).
//
// Amadeus holds nothing before /orders, so there is no hold to create and no
// server-side ancillary state to reconcile: every hook resolves against
// flightOffersPrice, and the step commits the whole selection in one reprice
// where the inline /detail bags panel fired one per toggle.

import { deep_clone, get_config } from '@/utilities';
import { log } from '@/utilities/commons';
import { as_number } from '@liveflight/providers/base/helpers';
import { is_infant } from '@liveflight/utilities/travelers';
import {
  build_committed_selections,
  flatten_selections,
} from '@liveflight/utilities/additional-services';
import { get_offer_details, fetch_extra_bags } from './upstream';
import { apply_seats_to_raw, seats_are_mandatory } from './seats';

/**
 * Segment ids per itinerary, in offer order.
 *
 * @param {Object} flight_offer
 * @returns {Array<string[]>}
 */
const itinerary_segments = (flight_offer) => {
  const { itineraries = [] } = flight_offer || {};
  return itineraries.map((itinerary) => {
    const { segments = [] } = itinerary || {};
    return segments.map((segment) => (segment || {}).id).filter(Boolean);
  });
};

/**
 * The groups of segments one pick covers. `bookableByItinerary` reads
 * literally: true divides into one purchase per itinerary, billed unit x pax
 * x itineraries; false is one indivisible purchase over the whole coverage,
 * billed unit x pax, which Amadeus refuses to sell in parts.
 *
 * @param {string[]} segment_ids the entry's coverage
 * @param {Array<string[]>} segments_by_itinerary
 * @param {boolean} by_itinerary
 * @returns {Array<string[]>}
 */
const purchase_units = (segment_ids, segments_by_itinerary, by_itinerary) => {
  if (!by_itinerary) return [segment_ids];
  const units = segments_by_itinerary
    .map((segments) =>
      segments.filter((segment_id) => segment_ids.includes(segment_id))
    )
    .filter((unit) => unit.length);
  // No readable itineraries: sell the entry's own coverage rather than
  // dropping the bag.
  return units.length ? units : [segment_ids];
};

/**
 * The product has a row of its own on every section the wizard opens. Rows
 * couple across sections (`requiredOnAllSegments`), so a product missing from
 * one section's dropdown is an option the user can never complete. When every
 * product skips the same itinerary (IB sells the outbound only), that section
 * never opens and the bag stays sellable: every buyer covers the same legs.
 *
 * @param {Object} wizard_catalog keyed by `${id}:${section}`
 * @param {string} id
 * @returns {boolean}
 */
const offered_on_every_section = (wizard_catalog, id) =>
  Object.keys(wizard_catalog).every(
    (row_key) =>
      !!wizard_catalog[`${id}:${wizard_catalog[row_key].segmentIds[0]}`]
  );

/**
 * Is this row the same purchase as a wider row of its product? Rows of one
 * id couple, so keeping both would quote the narrower one on top of the
 * wider one Amadeus bills for the same legs (AZ+LH: 130 + 200 for 200).
 *
 * @param {Object} wizard_catalog
 * @param {Object} row
 * @returns {boolean}
 */
const covered_by_wider_row = (wizard_catalog, row) =>
  Object.keys(wizard_catalog).some((key) => {
    const { id, segmentIds } = wizard_catalog[key];
    return (
      id === row.id &&
      segmentIds.length > row.segmentIds.length &&
      row.segmentIds.every((segment_id) => segmentIds.includes(segment_id))
    );
  });

/**
 * The ids of the other products that can occupy the same leg as this unit.
 * Read off the entries, not the keys: exclusive-group resolution matches
 * `incompatibleWith` against `id`.
 *
 * @param {Object} catalog bare catalog
 * @param {string[]} keys its keys
 * @param {string} own_id the row's own product id
 * @param {string[]} unit the row's segments
 * @returns {string[]}
 */
const conflicting_ids = (catalog, keys, own_id, unit) =>
  keys
    .map((key) => catalog[key])
    .filter((sibling) => {
      const { id, segmentIds = [] } = sibling;
      return (
        id !== own_id &&
        segmentIds.some((segment_id) => unit.includes(segment_id))
      );
    })
    .map((sibling) => sibling.id);

/**
 * Does the same product also sell in narrower entries that cover all of this
 * one? Then this entry is their bundle (TU prices it as their sum), and
 * keeping it would quote its whole price on its first leg.
 *
 * @param {Object} catalog bare catalog
 * @param {string[]} keys its keys
 * @param {Object} entry
 * @returns {boolean}
 */
const is_bundle_of_narrower = (catalog, keys, entry) => {
  const { id, segmentIds = [] } = entry;
  const pieces = keys
    .map((key) => catalog[key])
    .filter((other) => {
      const { segmentIds: other_ids = [] } = other;
      return (
        other.id === id &&
        other_ids.length < segmentIds.length &&
        other_ids.every((segment_id) => segmentIds.includes(segment_id))
      );
    });
  return segmentIds.every((segment_id) =>
    pieces.some((piece) => (piece.segmentIds || []).includes(segment_id))
  );
};

/**
 * Does the row already keyed here beat this look-alike? Wider coverage wins;
 * a tie falls to the segments, then the cheaper price, never to catalog
 * order: Amadeus reorders the keys between reprices, and the catalog the
 * wizard renders and the one it commits against come from different ones.
 *
 * @param {Object} existing row already keyed here
 * @param {Object} entry look-alike bare entry
 * @param {string[]} unit the look-alike's purchase unit
 * @returns {boolean}
 */
const row_outranks = (existing, entry, unit) => {
  const { segmentIds = [], price: existing_price = {} } = existing;
  if (segmentIds.length !== unit.length) {
    return segmentIds.length > unit.length;
  }
  const existing_segments = segmentIds.join(',');
  const unit_segments = unit.join(',');
  if (existing_segments !== unit_segments) {
    return existing_segments < unit_segments;
  }
  const { price: entry_price = {} } = entry;
  return as_number(existing_price.total) <= as_number(entry_price.total);
};

/**
 * Turn the bare ExtraBagCatalog into the catalog the wizard renders, picks
 * from, and books against. Amadeus ships ONE entry for the whole journey, so:
 *
 * - Rows are fanned into purchase units (see purchase_units). Offering a
 *   per-itinerary entry as one pick would under-quote it and make buying the
 *   outbound alone impossible.
 * - Keys are `${id}:${first segment of the unit}`, which is what
 *   build_committed_selections reconstructs off a committed offer, so
 *   re-entering the step shows the previous picks as selected.
 * - `incompatibleWith` links only the siblings sharing a segment: the wire
 *   carries one chargeableCheckedBags per segment, so two bags on one leg
 *   aren't representable, while two on disjoint legs price fine together.
 * - Every row is flagged `requiredOnAllSegments`, and ids are the wire's
 *   count + weight, so the same purchase couples across legs even when
 *   Amadeus sells each leg as its own entry. Amadeus refuses a traveler
 *   taking a different quantity on different itineraries and two travelers
 *   buying on different itinerary sets; coupling makes both unreachable.
 *
 * Prices stay per unit — the sidebar sums the coupled rows, which is what
 * the roll-up bills (unit x travelers x itineraries).
 *
 * @param {Object} catalog ExtraBagCatalog keyed by bare bag id
 * @param {Object} flight_offer
 * @returns {Object} catalog keyed by `${id}:${segmentIds[0]}`
 */
export const to_wizard_catalog = (catalog, flight_offer) => {
  const safe = catalog || {};
  const keys = Object.keys(safe);
  const segments_by_itinerary = itinerary_segments(flight_offer);
  const wizard_catalog = {};
  keys.forEach((key) => {
    const entry = safe[key];
    const { segmentIds = [], bookableByItinerary = false } = entry;
    if (!segmentIds.length || is_bundle_of_narrower(safe, keys, entry)) return;
    const units = purchase_units(
      segmentIds,
      segments_by_itinerary,
      bookableByItinerary
    );
    units.forEach((unit) => {
      const row_key = `${entry.id}:${unit[0]}`;
      // Look-alikes starting on the same leg are one choice: the wider
      // coverage stands for both.
      const existing = wizard_catalog[row_key];
      if (existing && row_outranks(existing, entry, unit)) return;
      wizard_catalog[row_key] = {
        ...entry,
        segmentIds: unit,
        incompatibleWith: conflicting_ids(safe, keys, entry.id, unit),
        requiredOnAllSegments: true,
      };
    });
  });
  const widest = Object.keys(wizard_catalog)
    .filter((row_key) => !covered_by_wider_row(wizard_catalog, wizard_catalog[row_key]))
    .reduce(
      (filtered, row_key) => ({ ...filtered, [row_key]: wizard_catalog[row_key] }),
      {}
    );
  return Object.keys(widest)
    .filter((row_key) => offered_on_every_section(widest, widest[row_key].id))
    .reduce(
      (filtered, row_key) => ({ ...filtered, [row_key]: widest[row_key] }),
      {}
    );
};

/**
 * The wizard catalog key a flattened pick refers to. A miss means the pick
 * names a segment the catalog doesn't key a row on (a committed bag's second
 * leg, say), already covered by its unit's own row.
 *
 * @param {Object} pick
 * @returns {string}
 */
const catalog_key_for_pick = (pick) => {
  const { id = '', flightSegments: [segment_id = ''] = [] } = pick || {};
  return `${id}:${segment_id}`;
};

/**
 * Same physical product? A booked-bag echo carries only count and weight,
 * which is also all the wire carries — and so all the id is made of.
 *
 * @param {Object} catalog_row
 * @param {Object} entry canonical `{type:'bag', ...}` entry
 * @returns {boolean}
 */
const describes_same_bag = (catalog_row, entry) => {
  const {
    quantity: row_quantity,
    weight: row_weight,
    weightUnit: row_unit = '',
  } = catalog_row || {};
  const {
    quantity: entry_quantity,
    weight: entry_weight,
    weightUnit: entry_unit = '',
  } = entry || {};
  if (as_number(row_quantity, 1) !== as_number(entry_quantity, 1)) return false;
  if (as_number(row_weight) !== as_number(entry_weight)) return false;
  if (String(row_unit).toLowerCase() !== String(entry_unit).toLowerCase()) {
    return false;
  }
  return true;
};

/**
 * The catalog entry a booked bag came from: sellable to this traveler, the
 * same product, on the segment the bag sits on. Look-alikes share an id, so
 * any of them will do.
 *
 * @param {Array<Object>} entries bare catalog entries
 * @param {Object} entry booked bag entry
 * @param {string} traveler_id
 * @param {string} segment_id segment this booked bag sits on
 * @returns {Object|null}
 */
const match_catalog_entry = (entries, entry, traveler_id, segment_id) =>
  entries.find((candidate) => {
    const { segmentIds = [], travelerIds = [] } = candidate;
    return (
      travelerIds.includes(String(traveler_id)) &&
      describes_same_bag(candidate, entry) &&
      segmentIds.includes(segment_id)
    );
  }) || null;

/**
 * Does the bag already carry one of our catalog ids? NDC echoes a booked bag
 * with an id of its own, which keys no wizard row, so it needs restamping
 * just like the id-less GDS echo.
 *
 * @param {Array<Object>} entries bare catalog entries
 * @param {Object} entry booked bag entry
 * @returns {boolean}
 */
const carries_catalog_id = (entries, entry) =>
  entries.some(({ id }) => id === entry.id);

/**
 * Recover the catalog id Amadeus drops. The priced offer echoes a booked bag
 * as `{quantity, descriptions}` only, and build_committed_selections skips an
 * entry without an id, which would make every committed bag invisible to the
 * wizard's diff.
 *
 * Matching runs off the BARE catalog: every fanned row shares its entry's id.
 *
 * @param {Object} flight_offer normalized offer
 * @param {Object} bags_catalog bare ExtraBagCatalog
 * @returns {Object} offer whose bag entries carry `id`
 */
const stamp_bag_ids = (flight_offer, bags_catalog) => {
  const entries = Object.keys(bags_catalog).map((key) => bags_catalog[key]);
  if (!entries.length) return flight_offer;
  const { travelerPricings = [] } = flight_offer || {};
  return {
    ...flight_offer,
    travelerPricings: travelerPricings.map((pricing) => {
      const { travelerId = '', fareDetailsBySegment = [] } = pricing || {};
      const needs_id = fareDetailsBySegment.some((fare_detail) => {
        const { additionalServices = [] } = fare_detail || {};
        const list = Array.isArray(additionalServices) ? additionalServices : [];
        return list.some(
          (entry) => entry && entry.type === 'bag' && !carries_catalog_id(entries, entry)
        );
      });
      if (!needs_id) return pricing;
      return {
        ...pricing,
        fareDetailsBySegment: fareDetailsBySegment.map((fare_detail) => {
          const { additionalServices = [] } = fare_detail || {};
          const list = Array.isArray(additionalServices)
            ? additionalServices
            : [];
          return {
            ...fare_detail,
            additionalServices: list.map((entry) => {
              if (!entry || entry.type !== 'bag' || carries_catalog_id(entries, entry)) return entry;
              const matched = match_catalog_entry(
                entries,
                entry,
                travelerId,
                fare_detail.segmentId
              );
              return matched ? { ...entry, id: matched.id } : entry;
            }),
          };
        }),
      };
    }),
  };
};

/**
 * Group the seat picks of a flattened diff into the per-segment map
 * apply_seats_to_raw consumes.
 *
 * @param {Array<Object>} picks flattened `{travelerId, type, id, flightSegments}`
 * @returns {Object} `{ [segmentId]: { [travelerId]: seatNumber } }`
 */
const seat_selections_from_picks = (picks) => {
  const selections = {};
  picks
    .filter((pick) => pick.type === 'seat')
    .forEach((pick) => {
      const { travelerId, id, flightSegments: [segment_id = ''] = [] } = pick;
      if (!segment_id || !travelerId || !id) return;
      if (!selections[segment_id]) selections[segment_id] = {};
      selections[segment_id][travelerId] = id;
    });
  return selections;
};

/**
 * Rebuild `_raw`'s additionalServices to match `picks` exactly. Declarative
 * on purpose — the wizard hands over the full desired state, so a de-selected
 * bag or seat disappears by not being re-stamped. Bags go on before seats
 * because the wipe clears `chargeableSeatNumber` too.
 *
 * Held infants are left untouched: their allowance rides on the associated
 * adult and Amadeus rejects services booked against them.
 *
 * @param {Object} raw_offer frozen raw Amadeus offer
 * @param {Array<Object>} picks flattened `{travelerId, type, id, ...}`
 * @param {Object} wizard_catalog catalog keyed by `${id}:${segmentIds[0]}`
 * @returns {Object} new raw offer
 */
const apply_picks_to_raw = (raw_offer, picks, wizard_catalog) => {
  const staged_raw = deep_clone(raw_offer);
  const { travelerPricings = [] } = staged_raw;
  travelerPricings.forEach((pricing) => {
    if (is_infant(pricing)) return;
    const { fareDetailsBySegment = [] } = pricing;
    fareDetailsBySegment.forEach((fare_detail) => {
      fare_detail.additionalServices = {};
    });
  });

  picks
    .filter((pick) => pick.type === 'bag')
    .forEach((pick) => {
      // The row, not the bare entry: its segmentIds are the one purchase
      // unit this pick bought, so the outbound bag never leaks onto the
      // return.
      const bag = wizard_catalog[catalog_key_for_pick(pick)];
      if (!bag) return;
      const pricing = travelerPricings.find(
        (traveler_pricing) =>
          String(traveler_pricing.travelerId) === String(pick.travelerId)
      );
      if (!pricing || is_infant(pricing)) return;
      const { segmentIds = [], quantity, weight, weightUnit } = bag;
      const { fareDetailsBySegment = [] } = pricing;
      fareDetailsBySegment.forEach((fare_detail) => {
        if (!segmentIds.includes(fare_detail.segmentId)) return;
        // Amadeus's own wire shape: the row's id is ours, not Amadeus's.
        fare_detail.additionalServices.chargeableCheckedBags = {
          quantity,
          weight,
          weightUnit,
        };
      });
    });

  return apply_seats_to_raw(staged_raw, seat_selections_from_picks(picks));
};

/**
 * Same identity compute_ancillary_diff uses, so entries derived from the
 * offer and entries derived from the catalog compare as equal.
 *
 * @param {Object} pick
 * @returns {string}
 */
const fingerprint = (pick) => {
  const {
    travelerId = '',
    type = '',
    id = '',
    flightSegments = [],
    quantity = 1,
  } = pick || {};
  const segments = flightSegments.slice().sort().join(',');
  return `${travelerId}|${type}|${id}|${segments}|${Number(quantity) || 1}`;
};

/**
 * The page-side selections an offer stands for. A bag row is one product,
 * picked or not, while the echo's `quantity` counts the bags inside it — so
 * summing it would read a "2 bags" pick as two of them. Keys without a row
 * (a unit's later segments) are dropped so a de-selection clears the unit.
 *
 * @param {Object} flight_offer
 * @param {Object} wizard_catalog
 * @returns {Object} selections, see build_committed_selections
 */
const committed_selections = (flight_offer, wizard_catalog) => {
  const selections = build_committed_selections(flight_offer);
  const bag = {};
  Object.keys(selections.bag).forEach((traveler_id) => {
    bag[traveler_id] = {};
    Object.keys(selections.bag[traveler_id]).forEach((row_key) => {
      if (wizard_catalog[row_key]) bag[traveler_id][row_key] = 1;
    });
  });
  return { ...selections, bag };
};

/**
 * What the offer currently carries, in the flat pick shape the wizard diffs
 * against. A bag is stamped on every segment of its unit, so dropping the
 * picks with no catalog key collapses each unit back to its own row and an
 * untouched selection diffs to nothing. Seats aren't catalog rows and pass
 * through.
 *
 * @param {Object} flight_offer
 * @param {Object} wizard_catalog
 * @returns {Array<Object>} flattened picks
 */
const committed_picks = (flight_offer, wizard_catalog) =>
  flatten_selections(committed_selections(flight_offer, wizard_catalog));

/**
 * The whole state the user wants once this commit lands. The applier is
 * declarative, so feeding it `to_add` alone would wipe every pick the user
 * simply left as it was.
 *
 * @param {Object} flight_offer
 * @param {{to_add?: Array<Object>, to_remove?: Array<Object>}} diff
 * @param {Object} wizard_catalog
 * @returns {Array<Object>} flattened picks
 */
const desired_picks = (flight_offer, diff, wizard_catalog) => {
  const { to_add = [], to_remove = [] } = diff || {};
  const removed = new Set(to_remove.map(fingerprint));
  const kept = committed_picks(flight_offer, wizard_catalog).filter(
    (pick) => !removed.has(fingerprint(pick))
  );
  const kept_prints = new Set(kept.map(fingerprint));
  return kept.concat(
    to_add.filter((pick) => !kept_prints.has(fingerprint(pick)))
  );
};

/**
 * Amadeus prices bags all-or-nothing: a combination it won't sell comes back
 * as a perfectly clean reprice with the CHECKED_BAGS roll-up simply missing,
 * never as an error.
 *
 * @param {Object} flight_offer repriced offer
 * @returns {boolean}
 */
const bags_were_priced = (flight_offer) => {
  const { price = {} } = flight_offer || {};
  const { additionalServices = [] } = price;
  if (!Array.isArray(additionalServices)) return false;
  return additionalServices.some(
    (service) => service && service.type === 'CHECKED_BAGS'
  );
};

/**
 * The wizard-facing bag catalog for an offer. `fetch_extra_bags` resolves off
 * the catalog the last flightOffersPrice cached, so this never hits the wire.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @returns {Promise<{bare: Object, wizard: Object}>}
 */
const wizard_bags_catalog = (provider, flight_offer) =>
  fetch_extra_bags(provider, flight_offer).then((catalog) => {
    const bare = catalog || {};
    return { bare, wizard: to_wizard_catalog(bare, flight_offer) };
  });

/**
 * Nothing is reserved until the flight order is created at checkout. The
 * offer id stands in as the booking id so the wizard's `held_booking.id`
 * guard passes; no expiry keeps the resume countdown dormant.
 *
 * @param {Provider} _provider
 * @param {Object} flight_offer
 * @returns {Promise<{bookingId: string, expiresAt: string, flight_offer: Object}>}
 */
export const book_held = (_provider, flight_offer) => {
  const { id = '' } = flight_offer || {};
  return Promise.resolve({ bookingId: String(id), expiresAt: '', flight_offer });
};

/**
 * Confirm the offer is still priceable and hand back a fresh total. The offer
 * is the only record of a pick until /orders, so `ancillaries` is read
 * straight off it — reporting it rather than an empty list is what lets the
 * page diff a de-selection into a removal.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @returns {Promise<{booking: null, flight_offer: Object, ancillaries: Array, expiresAt: string}>}
 */
export const retrieve = (provider, flight_offer) =>
  get_offer_details(provider, flight_offer).then((priced_flight_offer) =>
    wizard_bags_catalog(provider, priced_flight_offer).then(
      ({ bare, wizard }) => {
        // The page seeds its selections off this offer, and an unstamped
        // bag is invisible to both the seeder and the diff.
        const stamped = stamp_bag_ids(priced_flight_offer, bare);
        return {
          booking: null,
          flight_offer: stamped,
          ancillaries: committed_picks(stamped, wizard),
          selections: committed_selections(stamped, wizard),
          expiresAt: '',
        };
      }
    )
  );

/**
 * Bags + seatmap in one envelope, keyed by the wizard's step ids. Neither
 * side reprices. Empty slices are dropped so `steps()` collapses them.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @returns {Promise<Object>} `{ bag?, seat? }`
 */
export const fetch_ancillaries = (provider, flight_offer) => {
  const { travelerPricings = [] } = flight_offer || {};
  return Promise.all([
    wizard_bags_catalog(provider, flight_offer),
    provider.get_seatmap(flight_offer, {
      areInfantsPresent: travelerPricings.some(is_infant),
      allowMalformed: get_config(
        'guiSettings.customizations.allowMalformedDecks',
        false
      ),
      // A seatmap failure must not take the bag offer down with it.
    }).catch((e) => {
      log('[amadeus/ancillaries] fetch_ancillaries: seatmap failed', e);
      // A fare that mandates a seat has no way forward without the map, so
      // the page must exit, as /seat-selection does on an API error.
      if (seats_are_mandatory(flight_offer)) throw e;
      return null;
    }),
  ]).then(([{ wizard }, seatmap]) => {
    const envelope = {};
    if (Object.keys(wizard).length) envelope.bag = wizard;
    if (seatmap) envelope.seat = seatmap;
    return envelope;
  });
};

/**
 * The single reprice that commits the step: bags and seats land on the raw
 * offer in one pass and go up as one flightOffersPrice, which also refreshes
 * the bag catalog.
 *
 * `seats_priced` reports whether the reprice came back carrying every seat
 * that was picked — a seat can go while the user is still choosing, and
 * Amadeus answers with a clean reprice either way. True when no seat was
 * picked: there was nothing to lose.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @param {string} _booking_id
 * @param {{to_add?: Array<Object>, to_remove?: Array<Object>}} diff
 * @returns {Promise<{flight_offer: Object, expiresAt: string, seats_priced: boolean}>}
 */
export const book_ancillaries = (provider, flight_offer, _booking_id, diff) => {
  let picks = [];
  let seat_selections = {};
  return wizard_bags_catalog(provider, flight_offer)
    .then(({ bare, wizard }) => {
      // Stamp rather than trust the caller to hand over an offer `retrieve`
      // already stamped: an unstamped bag carries no id, so every bag the
      // user kept would silently drop out of the reprice.
      picks = desired_picks(stamp_bag_ids(flight_offer, bare), diff, wizard);
      seat_selections = seat_selections_from_picks(picks);
      // No stamping on the way out: committed bag ids are only read back
      // through `retrieve`, which restamps off the catalog this reprice
      // refreshes, so doing it here would risk baking in stale ids.
      return get_offer_details(provider, {
        ...flight_offer,
        _raw: apply_picks_to_raw(flight_offer._raw, picks, wizard),
      });
    })
    .then((priced_flight_offer) => {
      // Fail the commit rather than let the user walk to checkout believing
      // they bought a bag Amadeus quietly refused to price.
      if (
        picks.some((pick) => pick.type === 'bag') &&
        !bags_were_priced(priced_flight_offer)
      ) {
        throw new Error(
          '[AmadeusProvider] book_ancillaries: bag combination was not priced'
        );
      }
      let seats_priced = true;
      if (Object.keys(seat_selections).length) {
        seats_priced = provider.validate_seats_priced(
          flight_offer,
          seat_selections,
          priced_flight_offer
        );
      }
      return { flight_offer: priced_flight_offer, expiresAt: '', seats_priced };
    });
};
